/**
 * Fallback: arquivos como Blob no IndexedDB (PLAN §16).
 *
 * Usado apenas quando o OPFS não existe. É inferior por um motivo concreto: o blob
 * precisa estar inteiro em memória para ser gravado, o que é arriscado para vídeos
 * grandes num iPhone. Mantém a mesma interface, então o resto do app não sabe qual
 * das duas implementações está ativa.
 *
 * Fica num banco IndexedDB **separado** do banco de dados do app. Assim, "limpar
 * vídeos" e "reconstruir o índice" continuam sendo operações independentes.
 */
import Dexie, { type EntityTable } from 'dexie';
import {
  isQuotaError,
  StorageQuotaError,
  StorageWriteError,
  type OfflineFileStore,
  type WriteOptions,
} from '@/storage/types';

interface StoredFile {
  key: string;
  /**
   * Guardado como Uint8Array, não como Blob.
   *
   * O Safari tem histórico de problemas armazenando Blob diretamente no IndexedDB, e
   * o iPhone é a plataforma alvo. Uma view tipada é transportada de forma confiável
   * pelo structured clone e é aceita pelo construtor de Blob em qualquer ambiente —
   * um ArrayBuffer cru não é (o jsdom, por exemplo, o converte em string).
   */
  data: Uint8Array<ArrayBuffer>;
  mimeType: string;
  size: number;
  /** `true` enquanto o download não terminou. Equivale ao diretório temp do OPFS. */
  temp: boolean;
  updatedAt: string;
}

class MediaDatabase extends Dexie {
  files!: EntityTable<StoredFile, 'key'>;

  constructor(name: string) {
    super(name);
    this.version(1).stores({ files: 'key, temp' });
  }
}

export class IndexedDbBlobFileStore implements OfflineFileStore {
  readonly id = 'indexeddb-blob';

  private readonly db: MediaDatabase;

  constructor(databaseName = 'ccb-libras-media') {
    this.db = new MediaDatabase(databaseName);
  }

  isSupported(): boolean {
    return typeof indexedDB !== 'undefined';
  }

  async write(key: string, source: Response | Blob, options: WriteOptions = {}): Promise<void> {
    const { onProgress, signal } = options;

    try {
      let blob: Blob;
      let bytesWritten = 0;

      if (source instanceof Blob) {
        blob = source;
        bytesWritten = blob.size;
        onProgress?.({ bytesWritten, totalBytes: blob.size });
      } else {
        const totalBytes =
          options.expectedBytes ?? (Number(source.headers.get('content-length')) || undefined);

        const reader = source.body?.getReader();
        if (!reader) throw new StorageWriteError('A resposta não tem corpo legível.');

        // Acumula em memória — a limitação que justifica o OPFS como primário.
        const chunks: BlobPart[] = [];
        for (;;) {
          if (signal?.aborted) throw new DOMException('Download cancelado', 'AbortError');

          const { done, value } = await reader.read();
          if (done) break;
          if (!value) continue;

          chunks.push(value as unknown as BlobPart);
          bytesWritten += value.byteLength;
          onProgress?.({ bytesWritten, ...(totalBytes ? { totalBytes } : {}) });
        }

        blob = new Blob(chunks, { type: 'video/mp4' });
      }

      const data = new Uint8Array(await blob.arrayBuffer());

      // Uma única escrita, já definitiva.
      //
      // Aqui não existe o passo temp→media do OPFS, e não faz falta: o conteúdo é
      // acumulado inteiro em memória antes de tocar o banco, então o `put` é
      // atômico por construção — não há como um registro parcial ser gravado.
      // (Um `put` seguido de `update` para simular as duas fases seria pior que
      // inútil: o `update` do Dexie reescreve o registro e desmonta a view tipada.)
      await this.db.files.put({
        key,
        data,
        mimeType: blob.type || 'video/mp4',
        size: blob.size,
        temp: false,
        updatedAt: new Date().toISOString(),
      });
    } catch (error) {
      await this.db.files.delete(key).catch(() => undefined);

      if (isQuotaError(error)) {
        throw new StorageQuotaError(
          'Não há espaço suficiente no dispositivo para salvar este vídeo.',
          { cause: error },
        );
      }
      if (error instanceof DOMException && error.name === 'AbortError') throw error;

      throw new StorageWriteError(`Falha ao gravar o arquivo: ${(error as Error).message}`, {
        cause: error,
      });
    }
  }

  async exists(key: string): Promise<boolean> {
    const row = await this.db.files.get(key);
    return row !== undefined && !row.temp && row.size > 0;
  }

  async read(key: string): Promise<Blob> {
    const row = await this.db.files.get(key);
    if (!row || row.temp) throw new StorageWriteError(`Arquivo não encontrado: ${key}`);
    return new Blob([row.data], { type: row.mimeType });
  }

  async getSize(key: string): Promise<number | null> {
    const row = await this.db.files.get(key);
    return row && !row.temp ? row.size : null;
  }

  async remove(key: string): Promise<void> {
    await this.db.files.delete(key);
  }

  async list(): Promise<string[]> {
    const rows = await this.db.files.filter((row) => !row.temp).toArray();
    return rows.map((row) => row.key);
  }

  async totalSize(): Promise<number> {
    const rows = await this.db.files.filter((row) => !row.temp).toArray();
    return rows.reduce((sum, row) => sum + row.size, 0);
  }

  async clearTemp(): Promise<number> {
    const keys = (await this.db.files.filter((row) => row.temp).toArray()).map((row) => row.key);
    await this.db.files.bulkDelete(keys);
    return keys.length;
  }
}
